import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";
import { get, patch } from "@/lib/api";
import { readAgentPrefill, runPath } from "@/lib/moduleLinks";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { ErrorState, TableSkeleton } from "@/components/common/DataStates";
import { Button } from "@/components/ui/button";
import AgentRunForm from "./AgentRunForm";
import { AGENT_IDS } from "./agentForms";

const dateLabel = (value) => {
  if (!value || Number.isNaN(Date.parse(value))) return null;
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
};

function availability(agent, t, policies, canRun) {
  if (!agent.enabled) return t("agentsUi.unavailableDisabled");
  if (!agent.allowed_by_plan) return t("agents.notInPlan");
  if (!agent.executable || agent.contract?.execution_policy !== "draft") return t("agentsUi.runUnavailable");
  if (!canRun) return t("agentsUi.memberOnly");
  if (policies.isPending) return t("agentsUi.checkingPolicy");
  if (policies.isError || typeof policies.data?.external_actions_enabled !== "boolean") return t("agentsUi.policyError");
  if (policies.data.external_actions_enabled) return t("agentsUi.externalPolicyBlocked");
  return t("agentsUi.available");
}

function RecentRuns({ agentId, limit = 5, compact = false }) {
  const { t } = useTranslation();
  const { orgId } = useAuth();
  const runs = useQuery({
    queryKey: ["agent-runs", orgId, agentId, limit],
    queryFn: () => get("/agent-runs", { params: { agent_id: agentId, limit } }),
    enabled: !!orgId,
  });
  if (runs.isPending) return <p className="text-sm text-muted-foreground">{t("agentsUi.loadingRuns")}</p>;
  if (runs.isError || !Array.isArray(runs.data?.items)) return <p className="text-sm text-destructive" role="alert">{t("agentsUi.runsError")}</p>;
  if (!runs.data.items.length) return <p className="text-sm text-muted-foreground">{t("agents.noRuns")}</p>;
  if (compact) return <p className="text-sm text-muted-foreground">{t("agentsUi.lastRun")}: {dateLabel(runs.data.items[0].created_at) || t("agentsUi.unknown")} · {runs.data.items[0].status || t("agentsUi.unknown")}</p>;
  return <ul className="mt-3 divide-y divide-border" data-testid="agent-recent-runs">
    {runs.data.items.map((run) => <li key={run.id} className="flex min-w-0 flex-wrap justify-between gap-2 py-3 text-sm">
      <div className="min-w-0"><Link to={runPath(run.id) || "/app/ai?tab=runs"} className="font-medium text-primary hover:underline" data-testid={`agent-recent-run-${run.id}`}>{t(`agentsUi.taskNames.${run.task_type}`, { defaultValue: run.task_type })}</Link><span className="ml-2 text-muted-foreground">{dateLabel(run.created_at) || t("agentsUi.unknown")}</span></div>
      <span className="break-all text-muted-foreground">{run.status || t("agentsUi.unknown")}</span>
    </li>)}
  </ul>;
}

function AgentCard({ agent, status }) {
  const { t } = useTranslation();
  return <article className="flex min-w-0 flex-col rounded-xl border border-border bg-card p-5" data-testid={`agent-card-${agent.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div><h2 className="text-xl font-semibold">{agent.persona}</h2><p className="text-sm text-muted-foreground">{t(`agentsUi.roles.${agent.id}`)}</p></div>
      <span className="max-w-full rounded-full bg-muted px-2.5 py-1 text-xs font-medium">{status}</span>
    </div>
    <p className="mt-4 flex-1 text-sm leading-6">{t(`agentsUi.descriptions.${agent.id}`)}</p>
    <p className="mt-3 text-xs text-muted-foreground">{t("agentsUi.state")}: {agent.enabled ? t("agents.enabled") : t("agents.disabled")}</p>
    <div className="mt-3"><RecentRuns agentId={agent.id} limit={1} compact /></div>
    <Link to={`/app/agents/${agent.id}`} className="og-focus mt-5 inline-flex w-fit items-center gap-1 rounded-md text-sm font-medium text-primary hover:underline">
      {t("agentsUi.open")}<ArrowRight className="h-4 w-4" />
    </Link>
  </article>;
}

function AgentSettings({ agent }) {
  const { t } = useTranslation();
  const { orgId, hasRole } = useAuth();
  const client = useQueryClient();
  const save = useMutation({
    mutationFn: (changes) => patch(`/agents/${encodeURIComponent(agent.id)}/settings`, changes),
    onSuccess: () => client.invalidateQueries({ queryKey: ["agents", orgId] }),
  });
  const admin = hasRole("admin");
  return <section className="min-w-0 rounded-xl border border-border bg-card p-4 sm:p-6">
    <h2 className="text-lg font-semibold">{t("agentsUi.settings")}</h2>
    <p className="mt-2 text-sm">{t("agentsUi.state")}: <strong>{agent.enabled ? t("agents.enabled") : t("agents.disabled")}</strong></p>
    <div className="mt-4 flex flex-wrap items-center gap-3">
      <Button type="button" variant="outline" size="sm" disabled={!admin || save.isPending} onClick={() => save.mutate({ enabled: !agent.enabled })} data-testid="agent-toggle">
        {agent.enabled ? t("agentsUi.disable") : t("agentsUi.enable")}
      </Button>
      <label htmlFor="agent-mode" className="text-sm">{t("agents.mode")}</label>
      <select id="agent-mode" value={agent.mode} disabled={!admin || save.isPending}
        onChange={(event) => save.mutate({ mode: event.target.value })}
        className="og-focus min-w-0 max-w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
        <option value="MANUAL">{t("agents.modes.MANUAL")}</option>
        <option value="APPROVAL_REQUIRED">{t("agents.modes.APPROVAL_REQUIRED")}</option>
        {agent.mode === "AUTOMATIC" && <option value="AUTOMATIC" disabled>{t("agents.modes.AUTOMATIC")}</option>}
      </select>
    </div>
    <p className="mt-3 text-xs text-muted-foreground">{t("agentsUi.effectiveMode")}: {t(`agents.modes.${agent.effective_mode}`, { defaultValue: agent.effective_mode || t("agentsUi.unknown") })}</p>
    {!admin && <p className="mt-2 text-xs text-muted-foreground">{t("agentsUi.adminOnly")}</p>}
    {save.isError && <p role="alert" className="mt-2 text-sm text-destructive">{t("agentsUi.settingsError")}: {save.error?.message}</p>}
  </section>;
}

function AgentDetail({ agent }) {
  const { t } = useTranslation();
  const { search } = useLocation();
  const { orgId, hasRole } = useAuth();
  const prefill = readAgentPrefill(agent.id, search);
  const brain = useQuery({ queryKey: ["brand-brain", orgId], queryFn: () => get("/brand-brain"), enabled: !!orgId });
  const policies = useQuery({ queryKey: ["ai-policies", orgId], queryFn: () => get("/ai/policies"), enabled: !!orgId });
  const reason = !hasRole("member") ? t("agentsUi.memberOnly")
    : !agent.enabled ? t("agentsUi.unavailableDisabled")
      : !agent.allowed_by_plan ? t("agents.notInPlan")
        : !agent.executable || agent.contract?.execution_policy !== "draft" ? t("agentsUi.runUnavailable")
          : policies.isPending ? t("agentsUi.checkingPolicy")
            : policies.isError || typeof policies.data?.external_actions_enabled !== "boolean" ? t("agentsUi.policyError")
              : policies.data.external_actions_enabled ? t("agentsUi.externalPolicyBlocked") : null;
  const contexts = agent.contract?.required_context || [];
  return <div className="min-w-0 space-y-5" data-testid={`agent-detail-${agent.id}`}>
    <Link to="/app/agents" className="og-focus inline-flex items-center gap-1 rounded text-sm text-primary hover:underline"><ArrowLeft className="h-4 w-4" />{t("agentsUi.back")}</Link>
    <div><PageHeader title={`${agent.persona} · ${t(`agentsUi.roles.${agent.id}`)}`} subtitle={t(`agentsUi.descriptions.${agent.id}`)} /></div>
    <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
      <p className="text-sm font-medium">{reason || t("agentsUi.available")}</p>
      <p className="mt-2 flex gap-2 text-sm text-muted-foreground"><ShieldCheck className="h-4 w-4 shrink-0" />{policies.data?.external_actions_enabled === false ? t("agentsUi.externalOff") : policies.data?.external_actions_enabled === true ? t("agentsUi.externalOn") : t("agentsUi.externalUnknown")}</p>
      <p className="mt-2 text-sm text-muted-foreground">{t("agentsUi.approvalHint")}</p>
    </div>
    <div className="grid min-w-0 gap-5 lg:grid-cols-2">
      <section className="min-w-0 rounded-xl border border-border bg-card p-4 sm:p-6">
        <h2 className="text-lg font-semibold">{t("agentsUi.context")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t("agentsUi.contextHint")}</p>
        {brain.isPending ? <p className="mt-3 text-sm">{t("agentsUi.loadingBrain")}</p>
          : brain.isError || !brain.data?.readiness ? <p role="alert" className="mt-3 text-sm text-destructive">{t("agentsUi.brainError")}</p>
            : <p className="mt-3 text-sm">{t("agentsUi.brainReadiness")}: {brain.data.readiness.ready_for_agents ? t("agentsUi.ready") : t("agentsUi.incomplete")} · {brain.data.completeness_score ?? t("agentsUi.unknown")}%</p>}
        <ul className="mt-3 flex flex-wrap gap-2">
          {contexts.map((context) => {
            const section = context.replace("brand_brain:", "");
            const completeness = brain.data?.readiness?.sections?.[section];
            return <li key={context} className="rounded-md border border-border px-2.5 py-1 text-xs">
              {t(`agentsUi.sections.${section}`, { defaultValue: section })}{typeof completeness === "number" ? ` · ${completeness}%` : ""}
            </li>;
          })}
        </ul>
        <Link to="/app/brand-brain" className="mt-4 inline-block text-sm text-primary underline">{t("agentsUi.openBrain")}</Link>
      </section>
      <AgentSettings agent={agent} />
    </div>
    <AgentRunForm key={`${agent.id}:${prefill?.key || ""}`} agent={agent} allowed={!reason} reason={reason}
      prefill={prefill && agent.tasks.some((task) => task.task_type === prefill.taskType) ? prefill : null} />
    <section className="min-w-0 rounded-xl border border-border bg-card p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t("agentsUi.recentRuns")}</h2>
        <Link to="/app/ai?tab=runs" className="text-sm text-primary underline">{t("agentsUi.monitoring")}</Link>
      </div>
      <RecentRuns agentId={agent.id} />
    </section>
  </div>;
}

export default function AgentsPage() {
  const { t } = useTranslation();
  const { agentId } = useParams();
  const { orgId, hasRole } = useAuth();
  const catalogue = useQuery({ queryKey: ["agents", orgId], queryFn: () => get("/agents"), enabled: !!orgId });
  const policies = useQuery({ queryKey: ["ai-policies", orgId], queryFn: () => get("/ai/policies"), enabled: !!orgId });
  if (catalogue.isPending) return <TableSkeleton rows={6} />;
  if (catalogue.isError || !Array.isArray(catalogue.data)) return <ErrorState message={catalogue.error?.message} onRetry={catalogue.refetch} />;
  const agents = AGENT_IDS.map((id) => catalogue.data.find((entry) => entry.id === id));
  if (agents.some((agent) => !agent)) return <ErrorState message={t("agentsUi.catalogueIncomplete")} onRetry={catalogue.refetch} />;
  if (agentId) {
    const selected = agents.find((agent) => agent.id === agentId);
    return selected ? <AgentDetail agent={selected} /> : <ErrorState message={t("agentsUi.unknownAgent")} />;
  }
  return <div className="space-y-6" data-testid="agents-overview">
    <PageHeader title={t("agentsUi.title")} subtitle={t("agentsUi.subtitle")} />
    <div className="grid min-w-0 gap-4 md:grid-cols-2 xl:grid-cols-3">{agents.map((agent) => <AgentCard key={agent.id} agent={agent} status={availability(agent, t, policies, hasRole("member"))} />)}</div>
    <Link to="/app/ai?tab=runs" className="inline-flex text-sm text-primary underline">{t("agentsUi.monitoring")}</Link>
  </div>;
}